import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CalendarDays, Plus, Video, Phone, MapPin, Users, MessageCircle, Mail,
  Trash2, Edit, CheckSquare, Clock, X, Filter, ChevronDown, ChevronUp,
  ClipboardList, AlertCircle, FileText, Copy, Check
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { fmtDate, canDelete } from '../utils/helpers';
import BulkActionBar from '../components/BulkActionBar';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import { buildWaUrl } from '../utils/whatsapp';

// ── Constants ──────────────────────────────────────────────────────────────────
const MODES = ['In-Person', 'Video Call', 'Phone', 'Site Visit'];
const STATUSES = ['Scheduled', 'In Progress', 'Completed', 'Cancelled', 'Postponed'];
const DURATIONS = [15, 30, 45, 60, 90, 120, 180];

const MODE_META = {
  'In-Person':  { color: 'bg-blue-100 text-blue-700',   icon: <MapPin size={12} /> },
  'Video Call': { color: 'bg-green-100 text-green-700',  icon: <Video size={12} /> },
  'Phone':      { color: 'bg-yellow-100 text-yellow-700',icon: <Phone size={12} /> },
  'Site Visit': { color: 'bg-orange-100 text-orange-700',icon: <MapPin size={12} /> },
};
const STATUS_COLOR = {
  'Scheduled':   'bg-blue-50 text-blue-700 border-blue-200',
  'In Progress': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Completed':   'bg-green-50 text-green-700 border-green-200',
  'Cancelled':   'bg-gray-100 text-gray-500 border-gray-200',
  'Postponed':   'bg-orange-50 text-orange-700 border-orange-200',
};

function fmt12(time) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${ampm}`;
}

function locationLabel(mode) {
  if (mode === 'Video Call') return 'Meeting Link';
  if (mode === 'Phone') return 'Call Number';
  if (mode === 'Site Visit') return 'Site Address';
  return 'Venue';
}

// ── FormField wrapper — defined at module scope to prevent remount on every keystroke ──
function F({ label, children, req }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{req && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

// ── Schedule Meeting Modal ─────────────────────────────────────────────────────
function ScheduleModal({ editMeeting, staff, clients, onSaved, onClose }) {
  const { user } = useAuth();
  const isEdit = !!editMeeting;
  const [form, setForm] = useState({
    title: '', client_id: '', meeting_date: '', meeting_time: '10:00',
    duration_minutes: 60, mode: 'In-Person', location: '',
    agenda: '', assigned_to: user?.id || '', status: 'Scheduled',
    next_meeting_date: '',
    internal_attendees: [], external_attendees: [],
    ...editMeeting,
    internal_attendees: editMeeting?.attendees?.filter(a => a.attendee_type === 'Internal').map(a => String(a.user_id)) || [],
    external_attendees: editMeeting?.attendees?.filter(a => a.attendee_type === 'External').map(a => ({ name: a.external_name, email: a.external_email || '', mobile: a.external_mobile || '' })) || [],
  });
  const [clientSearch, setClientSearch] = useState(editMeeting?.client_name || '');
  const [clientSugs, setClientSugs] = useState([]);
  const [saving, setSaving] = useState(false);

  const searchClients = v => {
    setClientSearch(v);
    if (!v.trim()) { setClientSugs([]); setForm(f => ({ ...f, client_id: '' })); return; }
    const q = v.toLowerCase();
    setClientSugs(clients.filter(c => c.name.toLowerCase().includes(q) || c.client_code?.toLowerCase().includes(q)).slice(0, 7));
  };

  const addExternal = () => setForm(f => ({ ...f, external_attendees: [...f.external_attendees, { name: '', email: '', mobile: '', _k: Math.random() }] }));
  const removeExternal = i => setForm(f => ({ ...f, external_attendees: f.external_attendees.filter((_, j) => j !== i) }));
  const updateExternal = (i, field, v) => setForm(f => { const a = [...f.external_attendees]; a[i] = { ...a[i], [field]: v }; return { ...f, external_attendees: a }; });
  const toggleInternal = id => setForm(f => {
    const s = String(id);
    return { ...f, internal_attendees: f.internal_attendees.includes(s) ? f.internal_attendees.filter(x => x !== s) : [...f.internal_attendees, s] };
  });

  const handleSave = async e => {
    e.preventDefault();
    if (!form.title || !form.meeting_date || !form.meeting_time) { toast.error('Title, date and time are required'); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/meetings/${editMeeting.id}`, form);
      else await api.post('/meetings', form);
      toast.success(isEdit ? 'Meeting updated' : 'Meeting scheduled');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-end z-50">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-[#1e3a5f]">
          <h2 className="text-white font-bold text-lg flex items-center gap-2"><CalendarDays size={20} />{isEdit ? 'Edit Meeting' : 'Schedule Meeting'}</h2>
          <button onClick={onClose} className="text-blue-200 hover:text-white"><X size={22} /></button>
        </div>

        <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-6 space-y-5">
          <F label="Meeting Title" req><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="input-field" placeholder="e.g. Year-End ITR Review" required /></F>

          <F label="Client">
            <div className="relative">
              <input value={clientSearch} onChange={e => searchClients(e.target.value)} className="input-field" placeholder="Search client..." />
              {clientSugs.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-48 overflow-y-auto">
                  {clientSugs.map(c => (
                    <button key={c.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2"
                      onClick={() => { setForm(f => ({ ...f, client_id: c.id })); setClientSearch(c.name); setClientSugs([]); }}>
                      <span className="font-mono text-gray-400 text-xs w-20 flex-shrink-0">{c.client_code}</span>
                      <span className="font-medium">{c.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </F>

          <div className="grid grid-cols-2 gap-4">
            <F label="Date" req><input type="date" value={form.meeting_date} onChange={e => setForm(f => ({ ...f, meeting_date: e.target.value }))} className="input-field" required /></F>
            <F label="Time" req><input type="time" value={form.meeting_time} onChange={e => setForm(f => ({ ...f, meeting_time: e.target.value }))} className="input-field" required /></F>
            <F label="Duration">
              <select value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: Number(e.target.value) }))} className="input-field">
                {DURATIONS.map(d => <option key={d} value={d}>{d < 60 ? `${d} min` : `${d/60}h${d%60 ? ` ${d%60}m` : ''}`}</option>)}
              </select>
            </F>
            <F label="Mode">
              <select value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value, location: '' }))} className="input-field">
                {MODES.map(m => <option key={m}>{m}</option>)}
              </select>
            </F>
          </div>

          <F label={locationLabel(form.mode)}>
            <div className="space-y-2">
              {form.mode === 'Video Call' && (
                <div className="flex gap-2 flex-wrap">
                  <button type="button" onClick={() => {
                    const chars = 'abcdefghijklmnopqrstuvwxyz';
                    const seg = n => Array.from({length: n}, () => chars[Math.floor(Math.random()*26)]).join('');
                    const link = `https://meet.google.com/${seg(3)}-${seg(4)}-${seg(3)}`;
                    setForm(f => ({ ...f, location: link }));
                    navigator.clipboard?.writeText(link).catch(() => {});
                    toast.success('Google Meet link generated & copied!');
                  }} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#00897b] text-white rounded-lg hover:bg-[#00796b] transition-colors font-medium">
                    <Video size={13} /> Google Meet
                  </button>
                  <button type="button" onClick={() => {
                    const rand = () => Math.random().toString(36).substring(2, 10);
                    const link = `https://teams.microsoft.com/l/meetup-join/19%3Ameeting_${rand()}${rand()}%40thread.v2/0?context=%7B%7D`;
                    setForm(f => ({ ...f, location: link }));
                    navigator.clipboard?.writeText(link).catch(() => {});
                    toast.success('Teams link generated & copied!');
                  }} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#5558af] text-white rounded-lg hover:bg-[#4a4d9e] transition-colors font-medium">
                    <Video size={13} /> Teams
                  </button>
                  <button type="button" onClick={() => {
                    const meetingId = String(Math.floor(1000000000 + Math.random() * 9000000000));
                    const link = `https://zoom.us/j/${meetingId}`;
                    setForm(f => ({ ...f, location: link }));
                    navigator.clipboard?.writeText(link).catch(() => {});
                    toast.success('Zoom link generated & copied!');
                  }} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#2d8cff] text-white rounded-lg hover:bg-[#1a7ae8] transition-colors font-medium">
                    <Video size={13} /> Zoom
                  </button>
                </div>
              )}
              <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="input-field"
                placeholder={form.mode === 'Video Call' ? 'https://meet.google.com/...' : form.mode === 'Phone' ? 'Phone number' : 'Address / venue name'} />
            </div>
          </F>

          <div className="grid grid-cols-2 gap-4">
            <F label="Assigned To">
              <select value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} className="input-field">
                <option value="">— Select —</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </F>
            <F label="Status">
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="input-field">
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
              <label className="flex items-center gap-2 mt-2 text-xs text-gray-600 cursor-pointer">
                <input type="checkbox" checked={!!form.create_task} onChange={e => setForm(f => ({ ...f, create_task: e.target.checked }))} className="rounded" />
                Create linked task (auto-completes with the meeting)
              </label>
            </F>
          </div>

          <F label="Agenda / Discussion Points">
            <textarea value={form.agenda} onChange={e => setForm(f => ({ ...f, agenda: e.target.value }))} className="input-field" rows={4} placeholder="1. Review ITR computation&#10;2. Discuss TDS challans&#10;3. Q&A" />
          </F>

          <F label="Next Meeting Date (optional)">
            <input type="date" value={form.next_meeting_date} onChange={e => setForm(f => ({ ...f, next_meeting_date: e.target.value }))} className="input-field" />
          </F>

          {/* Internal Attendees */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Internal Attendees (Staff)</label>
            <div className="flex flex-wrap gap-2">
              {staff.map(s => {
                const sel = form.internal_attendees.includes(String(s.id));
                return (
                  <button key={s.id} type="button" onClick={() => toggleInternal(s.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${sel ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#1e3a5f]'}`}>
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* External Attendees */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600">External Attendees (Clients / Others)</label>
              <button type="button" onClick={addExternal} className="text-xs text-[#1e3a5f] hover:text-[#f0a500] flex items-center gap-1"><Plus size={12} /> Add</button>
            </div>
            {form.external_attendees.map((ea, i) => (
              <div key={ea._k ?? i} className="flex gap-2 mb-2 items-center">
                <input value={ea.name} onChange={e => updateExternal(i, 'name', e.target.value)} placeholder="Name *" className="input-field flex-1 text-sm py-1.5" />
                <input value={ea.mobile} onChange={e => updateExternal(i, 'mobile', e.target.value)} placeholder="Mobile" className="input-field w-28 text-sm py-1.5" />
                <input value={ea.email} onChange={e => updateExternal(i, 'email', e.target.value)} placeholder="Email" className="input-field flex-1 text-sm py-1.5" />
                <button type="button" onClick={() => removeExternal(i)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
              </div>
            ))}
          </div>
        </form>

        <div className="px-6 py-4 border-t flex gap-3">
          <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary py-2.5 flex items-center justify-center gap-2">
            <CalendarDays size={16} />{saving ? 'Saving…' : isEdit ? 'Update Meeting' : 'Schedule Meeting'}
          </button>
          <button onClick={onClose} className="flex-1 btn-secondary py-2.5">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Meeting Card ──────────────────────────────────────────────────────────────
function MeetingCard({ m, onEdit, onDelete, onWA, isSelected, onToggleSel }) {
  const navigate = useNavigate();
  const isOverdue = m.status === 'Scheduled' && m.meeting_date < new Date().toISOString().split('T')[0];
  const modeMeta = MODE_META[m.mode] || MODE_META['In-Person'];

  return (
    <div className={`bg-white rounded-xl border shadow-sm hover:shadow-md hover:border-[#f0a500] transition-all cursor-pointer p-4 ${isOverdue ? 'border-l-4 border-l-red-400' : 'border-gray-100'} ${isSelected ? 'ring-2 ring-blue-300 bg-blue-50/30' : ''}`}
      onClick={() => navigate(`/meetings/${m.id}`)}>
      <div className="flex items-start gap-3">
        {onToggleSel && (
          <input type="checkbox" checked={!!isSelected} onClick={e => e.stopPropagation()}
            onChange={() => onToggleSel(m.id)} className="rounded mt-1 flex-shrink-0" />
        )}
        {/* Date block */}
        <div className="flex-shrink-0 w-12 text-center">
          <div className="text-xs text-gray-400 uppercase">{new Date(m.meeting_date + 'T00:00').toLocaleDateString('en-IN', { month: 'short' })}</div>
          <div className="text-2xl font-bold text-[#1e3a5f] leading-none">{new Date(m.meeting_date + 'T00:00').getDate()}</div>
          <div className="text-xs text-gray-500 mt-0.5">{fmt12(m.meeting_time)}</div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <Link to={`/meetings/${m.id}`} className="font-semibold text-gray-900 hover:text-[#1e3a5f] text-sm leading-tight">{m.title}</Link>
              {m.client_name && <div className="text-xs text-gray-500 mt-0.5">{m.client_name}{m.client_code ? ` · ${m.client_code}` : ''}</div>}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLOR[m.status] || ''}`}>{m.status}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${modeMeta.color}`}>{modeMeta.icon}{m.mode}</span>
            {m.duration_minutes && <span className="text-xs text-gray-400 flex items-center gap-1"><Clock size={11} />{m.duration_minutes}m</span>}
            {m.assigned_to_name && <span className="text-xs text-gray-500">• {m.assigned_to_name}</span>}
            {m.total_actions > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${m.pending_actions > 0 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                {m.pending_actions}/{m.total_actions} actions
              </span>
            )}
            {m.status === 'Completed' && !m.mom_sent && (
              <span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full border border-red-200">MOM pending</span>
            )}
          </div>

          {m.location && <div className="text-xs text-gray-400 mt-1 truncate">{locationLabel(m.mode)}: {m.location}</div>}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 mt-3 pt-3 border-t border-gray-50" onClick={e => e.stopPropagation()}>
        <Link to={`/meetings/${m.id}#mom`} className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#1e3a5f] px-2 py-1 rounded hover:bg-gray-50"><FileText size={12} /> MOM</Link>
        {m.client_whatsapp && (
          <button onClick={() => onWA(m)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-green-600 px-2 py-1 rounded hover:bg-gray-50"><MessageCircle size={12} /> WA</button>
        )}
        {m.client_email && (
          <a href={`mailto:${m.client_email}?subject=Meeting - ${m.title}`} className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 px-2 py-1 rounded hover:bg-gray-50"><Mail size={12} /> Email</a>
        )}
        <button onClick={() => onDelete(m)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 px-2 py-1 rounded hover:bg-gray-50 ml-auto"><Trash2 size={12} /> Delete</button>
      </div>
    </div>
  );
}

// ── Group — defined at module scope to prevent remount on parent re-render ─────
function Group({ label, items, onEdit, onDelete, onWA, selected, onToggleSel }) {
  if (!items.length) return null;
  return (
    <div>
      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
        <div className="h-px flex-1 bg-gray-100" />{label} ({items.length})<div className="h-px flex-1 bg-gray-100" />
      </h3>
      <div className="space-y-3">
        {items.map(m => <MeetingCard key={m.id} m={m} onEdit={() => onEdit(m)} onDelete={onDelete} onWA={onWA}
          isSelected={selected?.has(m.id)} onToggleSel={onToggleSel} />)}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MeetingsPage() {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [stats, setStats] = useState({});
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMeeting, setEditMeeting] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pastOpen, setPastOpen] = useState(false);

  const [filters, setFilters] = useState({ search: '', status: '', mode: '', from: '', to: '', assigned_to: '' });
  // Bulk select + archive
  const [selected, setSelected] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      if (showArchived) params.show_archived = '1';
      const [mr, sr] = await Promise.all([api.get('/meetings', { params }), api.get('/meetings/stats')]);
      setMeetings(mr.data); setStats(sr.data); setSelected(new Set());
    } catch { toast.error('Failed to load meetings'); }
    finally { setLoading(false); }
  }, [filters, showArchived]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/auth/users').then(r => setStaff(r.data)).catch(() => {});
    api.get('/clients').then(r => setClients(r.data)).catch(() => {});
  }, []);

  const handleDelete = async () => {
    try { await api.delete(`/meetings/${deleteTarget.id}`); toast.success('Meeting deleted'); setDeleteTarget(null); load(); }
    catch { toast.error('Delete failed'); }
  };

  const openWA = m => {
    const msg = `Dear ${m.client_name || 'Sir/Madam'},\n\nThis is a reminder for our meeting scheduled on ${fmtDate(m.meeting_date)} at ${fmt12(m.meeting_time)}.\nMode: ${m.mode}${m.location ? `\n${locationLabel(m.mode)}: ${m.location}` : ''}\n\n${m.agenda ? `Agenda:\n${m.agenda}` : ''}\n\nRegards,\n${m.assigned_to_name || user?.name}\nKrishna Uday & Associates`;
    const url = buildWaUrl(m.client_whatsapp, msg);
    if (url) window.open(url, '_blank');
  };

  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const weekEnd = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
  const nextWeekEnd = new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0];

  const grouped = useMemo(() => {
    const g = { today: [], tomorrow: [], thisWeek: [], nextWeek: [], past: [] };
    meetings.forEach(m => {
      if (m.meeting_date === today) g.today.push(m);
      else if (m.meeting_date === tomorrow) g.tomorrow.push(m);
      else if (m.meeting_date > tomorrow && m.meeting_date <= weekEnd) g.thisWeek.push(m);
      else if (m.meeting_date > weekEnd && m.meeting_date <= nextWeekEnd) g.nextWeek.push(m);
      else g.past.push(m);
    });
    return g;
  }, [meetings, today, tomorrow, weekEnd, nextWeekEnd]);

  const hasFilters = Object.values(filters).some(v => v);
  const clearFilters = () => setFilters({ search: '', status: '', mode: '', from: '', to: '', assigned_to: '' });

  const handleEdit = useCallback(m => { setEditMeeting(m); setShowModal(true); }, []);

  // ── Bulk actions ────────────────────────────────────────────────────────────
  const toggleSel = useCallback(id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }), []);
  const bulkArchive = async archive => {
    try { await api.post('/meetings/bulk-archive', { ids: [...selected], archive }); toast.success(archive ? 'Archived' : 'Unarchived'); load(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };
  const bulkDelete = async () => {
    try { await api.post('/meetings/bulk-delete', { ids: [...selected] }); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };
  const bulkUpdate = async updates => {
    try { await api.post('/meetings/bulk-update', { ids: [...selected], updates }); toast.success('Updated'); load(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f] flex items-center gap-2"><CalendarDays size={24} /> Meetings</h1>
          <p className="text-gray-500 text-sm">Client meetings, MOM & action items</p>
        </div>
        <div className="flex gap-2">
          <Link to="/meetings/action-items" className="btn-secondary flex items-center gap-2"><ClipboardList size={15} /> Action Items</Link>
          <button onClick={() => { setEditMeeting(null); setShowModal(true); }} className="btn-primary flex items-center gap-2"><Plus size={16} /> Schedule Meeting</button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Today's Meetings", value: stats.today || 0, color: 'border-blue-500', icon: <CalendarDays size={18} className="text-blue-500" /> },
          { label: 'This Week', value: stats.this_week || 0, color: 'border-indigo-400', icon: <CalendarDays size={18} className="text-indigo-400" /> },
          { label: 'Pending Actions', value: stats.pending_actions || 0, color: 'border-orange-500', icon: <ClipboardList size={18} className="text-orange-500" /> },
          { label: 'MOM Pending', value: stats.mom_pending || 0, color: 'border-red-500', icon: <AlertCircle size={18} className="text-red-500" /> },
        ].map(c => (
          <div key={c.label} className={`card flex items-center gap-3 border-l-4 ${c.color}`}>
            {c.icon}
            <div>
              <div className="text-2xl font-bold text-gray-800">{c.value}</div>
              <div className="text-xs text-gray-500">{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} className="input-field text-sm" placeholder="Search client or title..." />
          </div>
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="input-field w-36 text-sm">
            <option value="">All Statuses</option>{STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filters.mode} onChange={e => setFilters(f => ({ ...f, mode: e.target.value }))} className="input-field w-32 text-sm">
            <option value="">All Modes</option>{MODES.map(m => <option key={m}>{m}</option>)}
          </select>
          <select value={filters.assigned_to} onChange={e => setFilters(f => ({ ...f, assigned_to: e.target.value }))} className="input-field w-36 text-sm">
            <option value="">All Staff</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} className="input-field w-36 text-sm" />
          <input type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} className="input-field w-36 text-sm" />
          {hasFilters && <button onClick={clearFilters} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1 border border-red-200 rounded-lg"><X size={12} /> Clear</button>}
          <span className="text-xs text-gray-400 ml-auto">{meetings.length} meetings</span>
        </div>
      </div>

      {/* Bulk bar + selection controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => setShowArchived(a => !a)}
          className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${showArchived ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`}>
          {showArchived ? '📂 Archived' : '📁 Show Archived'}
        </button>
        {meetings.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
            <input type="checkbox" checked={meetings.length > 0 && selected.size === meetings.length}
              onChange={e => setSelected(e.target.checked ? new Set(meetings.map(m => m.id)) : new Set())}
              className="rounded" />
            Select all ({meetings.length})
          </label>
        )}
      </div>
      <BulkActionBar module="meetings" count={selected.size} staff={staff}
        statuses={['Scheduled', 'In Progress', 'Completed', 'Cancelled']}
        onAssign={id => bulkUpdate({ assigned_to: id })}
        onStatus={s => bulkUpdate({ status: s })}
        onArchive={archive => bulkArchive(archive)}
        archivedView={showArchived}
        onDelete={bulkDelete}
        onClear={() => setSelected(new Set())} />

      {/* Meeting list */}
      {loading ? (
        <div className="card text-center py-12 text-gray-400">Loading meetings...</div>
      ) : meetings.length === 0 ? (
        <div className="card text-center py-12">
          <CalendarDays size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-400">{showArchived ? 'No archived meetings.' : 'No meetings found.'}</p>
          {!showArchived && <button onClick={() => { setEditMeeting(null); setShowModal(true); }} className="btn-primary mt-4 flex items-center gap-2 mx-auto"><Plus size={15} /> Schedule First Meeting</button>}
        </div>
      ) : (
        <div className="space-y-6">
          <Group label="Today" items={grouped.today} onEdit={handleEdit} onDelete={setDeleteTarget} onWA={openWA} selected={selected} onToggleSel={toggleSel} />
          <Group label="Tomorrow" items={grouped.tomorrow} onEdit={handleEdit} onDelete={setDeleteTarget} onWA={openWA} selected={selected} onToggleSel={toggleSel} />
          <Group label="This Week" items={grouped.thisWeek} onEdit={handleEdit} onDelete={setDeleteTarget} onWA={openWA} selected={selected} onToggleSel={toggleSel} />
          <Group label="Next Week" items={grouped.nextWeek} onEdit={handleEdit} onDelete={setDeleteTarget} onWA={openWA} selected={selected} onToggleSel={toggleSel} />

          {grouped.past.length > 0 && (
            <div>
              <button onClick={() => setPastOpen(o => !o)}
                className="w-full flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 hover:text-gray-600">
                <div className="h-px flex-1 bg-gray-100" />
                {pastOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                Past Meetings ({grouped.past.length})
                <div className="h-px flex-1 bg-gray-100" />
              </button>
              {pastOpen && (
                <div className="space-y-3">
                  {grouped.past.map(m => <MeetingCard key={m.id} m={m} onEdit={() => handleEdit(m)} onDelete={setDeleteTarget} onWA={openWA}
                    isSelected={selected.has(m.id)} onToggleSel={toggleSel} />)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showModal && (
        <ScheduleModal
          editMeeting={editMeeting}
          staff={staff}
          clients={clients}
          onSaved={() => { setShowModal(false); setEditMeeting(null); load(); }}
          onClose={() => { setShowModal(false); setEditMeeting(null); }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          variant="delete"
          message={`Delete meeting "${deleteTarget.title}"? Action items will also be removed.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
